import React, { useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { services } from '../data/services';
import { faqs } from '../data/content';
import { Button } from '../components/Button';
import { FAQAccordion } from '../components/FAQAccordion';
import { GlowOrb } from '../components/GlowOrb';
import { getServiceImage } from '../data/images';
import { CheckCircle2, Clock, CreditCard, Sparkles, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
export function ServiceDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const service = services.find(s => s.slug === slug);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!service) {
    return <Navigate to="/services" replace data-id="element-844" />;
  }
  const Icon = service.icon;
  const relatedServices = services.filter(s => s.id !== service.id).slice(0, 3);
  return <div className="pb-24 relative" data-id="element-845">
      <GlowOrb color="primary" size="xl" position="-top-40 right-0" data-id="element-846" />

      {/* Hero */}
      <section className="pt-32 pb-24 relative z-10" data-id="element-847">
        <div className="container mx-auto px-6" data-id="element-848">
          <Link to="/services" className="inline-flex items-center gap-2 text-gold hover:text-white transition-colors mb-12 text-sm font-medium uppercase tracking-wider" data-id="element-849">
            <ArrowLeft className="w-4 h-4" data-id="element-850" /> Back to Services
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-851">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8
          }} data-id="element-852">
              <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full glass border-gold/30 mb-8" data-id="element-853">
                <Icon className="w-5 h-5 text-gold" data-id="element-854" />
                <span className="text-xs font-bold tracking-[0.2em] uppercase text-gold" data-id="element-855">
                  Sacred Healing Service
                </span>
              </div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif mb-8 text-white leading-tight" data-id="element-856">
                {service.title}
              </h1>
              <p className="text-xl text-beige-muted mb-12 leading-relaxed font-light" data-id="element-857">
                {service.shortDescription}
              </p>

              <div className="flex flex-wrap items-center gap-6 mb-12" data-id="element-858">
                <div className="glass px-6 py-3 rounded-full flex items-center gap-3" data-id="element-859">
                  <Clock className="w-5 h-5 text-gold" data-id="element-860" />
                  <span className="text-white font-medium" data-id="element-861">
                    {service.duration}
                  </span>
                </div>
                <div className="glass px-6 py-3 rounded-full flex items-center gap-3" data-id="element-862">
                  <CreditCard className="w-5 h-5 text-gold" data-id="element-863" />
                  <span className="text-white font-medium" data-id="element-864">
                    ${service.price}
                  </span>
                </div>
              </div>

              <Button href={`/booking?service=${service.slug}`} size="lg" variant="primary" data-id="element-865">
                Book This Session
              </Button>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1
          }} className="relative hidden lg:block h-[600px] rounded-[3rem] overflow-hidden border border-gold/20 shadow-2xl shadow-primary/30" data-id="element-866">
              <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: `url(${getServiceImage(service.slug)})`
            }} data-id="element-867"></div>
              <div className="absolute inset-0 bg-gradient-to-tr from-midnight/80 via-midnight/20 to-transparent" data-id="element-868"></div>
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-midnight/60" data-id="element-869"></div>
              {/* Floating service icon */}
              <div className="absolute bottom-10 left-10 right-10 flex items-center gap-4" data-id="element-870">
                <div className="w-20 h-20 rounded-full glass border-gold/30 flex items-center justify-center backdrop-blur-xl" data-id="element-871">
                  <Icon className="w-10 h-10 text-gold" data-id="element-872" />
                </div>
                <div data-id="element-873">
                  <div className="text-xs font-bold uppercase tracking-widest text-gold mb-1" data-id="element-874">
                    A Sacred Practice
                  </div>
                  <div className="font-serif text-2xl text-white" data-id="element-875">
                    {service.title}
                  </div>
                </div>
              </div>
              {/* Corner ornaments */}
              <div className="absolute top-6 left-6 w-12 h-12 border-l border-t border-gold/50" data-id="element-876"></div>
              <div className="absolute top-6 right-6 w-12 h-12 border-r border-t border-gold/50" data-id="element-877"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="container mx-auto px-6 relative z-20" data-id="element-878">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-879">
          {/* Main Details */}
          <div className="lg:col-span-2 space-y-8" data-id="element-880">
            <div className="glass p-8 md:p-12 rounded-[2.5rem]" data-id="element-881">
              <h2 className="text-3xl font-serif text-white mb-8 flex items-center gap-4" data-id="element-882">
                <Sparkles className="w-8 h-8 text-gold" data-id="element-883" />
                What to Expect
              </h2>
              <ul className="space-y-6" data-id="element-884">
                {service.whatToExpect.map((item, i) => <motion.li key={i} initial={{
                opacity: 0,
                x: -10
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: i * 0.1
              }} className="flex items-start gap-4 text-beige-muted" data-id="element-885">
                    <span className="w-2 h-2 rounded-full bg-gold mt-2.5 flex-shrink-0 shadow-[0_0_8px_rgba(212,175,110,0.8)]" data-id="element-886" />
                    <span className="text-lg leading-relaxed" data-id="element-887">{item}</span>
                  </motion.li>)}
              </ul>
            </div>

            <div className="glass p-8 md:p-12 rounded-[2.5rem]" data-id="element-888">
              <h2 className="text-3xl font-serif text-white mb-8 flex items-center gap-4" data-id="element-889">
                <CheckCircle2 className="w-8 h-8 text-gold" data-id="element-890" />
                Transformational Benefits
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-891">
                {service.benefits.map((benefit, i) => <motion.div key={i} initial={{
                opacity: 0,
                y: 10
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: i * 0.1
              }} className="bg-white/5 p-6 rounded-2xl border border-white/5" data-id="element-892">
                    <p className="text-beige font-medium" data-id="element-893">{benefit}</p>
                  </motion.div>)}
              </div>
            </div>

            {/* FAQ */}
            <div className="pt-12" data-id="element-894">
              <h2 className="text-3xl font-serif text-white mb-8" data-id="element-895">
                Common Questions
              </h2>
              <FAQAccordion items={faqs.slice(0, 3)} data-id="element-896" />
            </div>
          </div>

          {/* Sticky Sidebar */}
          <div className="space-y-8 lg:sticky lg:top-32 h-fit" data-id="element-897">
            <div className="glass p-8 rounded-[2rem] border-gold/20 relative overflow-hidden" data-id="element-898">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gold/10 rounded-full blur-2xl" data-id="element-899"></div>
              <h3 className="text-xl font-serif text-gold mb-4 relative z-10" data-id="element-900">
                Ideal For
              </h3>
              <p className="text-beige-muted leading-relaxed italic relative z-10 text-lg" data-id="element-901">
                "{service.idealFor}"
              </p>
            </div>

            <div className="bg-gradient-to-b from-primary to-midnight-panel p-8 rounded-[2rem] text-center border border-white/10 relative overflow-hidden" data-id="element-902">
              <div className="absolute inset-0 bg-star-dust opacity-30 mix-blend-screen" data-id="element-903"></div>
              <div className="relative z-10" data-id="element-904">
                <h3 className="text-2xl font-serif text-white mb-4" data-id="element-905">
                  Ready to begin?
                </h3>
                <p className="text-beige-muted mb-8 text-sm" data-id="element-906">
                  Secure your space today. Sessions are conducted via secure
                  video call.
                </p>
                <Button href={`/booking?service=${service.slug}`} fullWidth variant="primary" data-id="element-907">
                  Reserve Space
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
}